import ClearIcon from '@mui/icons-material/Clear';

import { DrawingManagerContext } from '../context/drawing-manager-context.js';
import { ToolbarItem } from './toolbar-item.js';

import type { DrawingManager, ISelection } from '@cadstrom/renderer';
import type { ElementType } from 'react';
import type { DrawingManagerContextValue } from '../context/drawing-manager-context.js';
import type { IToolbarItem, IToolbarItemState, ToolbarItemProps } from './toolbar-item.js';

interface IOwnState extends IToolbarItemState {}

export class ClearSelectionToolbarItem extends ToolbarItem<IOwnState> implements IToolbarItem {
	public static readonly ItemId = 'clearselect';
	id: string = ClearSelectionToolbarItem.ItemId;
	title = 'Clear selection';
	keyEventName = 'CLEAR_SELECT';
	defaultKeyBinding = 'esc';
	iconElement: ElementType = ClearIcon;
	includeInHistory = false;
	isExclusive = false;
	// @ts-expect-error
	context: DrawingManagerContextValue | undefined;
	oneShot = true;

	/** The drawing manager whose selection changes currently drive the button's enabled state. */
	private subscribedDrawingManager: DrawingManager | undefined;

	public constructor(props: ToolbarItemProps) {
		super(props);
		this.defaultState = {
			...this.defaultState,
			buttonProps: {
				disabled: !this.context?.drawingManager?.hasSelection(),
			},
		};
	}

	public override componentDidMount() {
		if (super.componentDidMount) {
			super.componentDidMount();
		}

		this.syncSelectionSubscription();
	}

	/**
	 * Keeps the subscription pointed at the context's current drawing manager after a re-render.
	 *
	 * @param prevProps - The props this component rendered with before the update.
	 */
	public override componentDidUpdate(prevProps: ToolbarItemProps): void {
		super.componentDidUpdate(prevProps);
		this.syncSelectionSubscription();
	}

	public override componentWillUnmount(): void {
		super.componentWillUnmount();
		this.unsubscribeFromSelectionChanged();
	}

	/** Subscribes to the context's current drawing manager and refreshes the button's enabled state. */
	private syncSelectionSubscription(): void {
		const drawingManager = this.context?.drawingManager;
		if (drawingManager === this.subscribedDrawingManager) {
			return;
		}

		this.unsubscribeFromSelectionChanged();
		if (drawingManager) {
			drawingManager.on('selectionChanged', this.handleSelectionChanged);
			this.subscribedDrawingManager = drawingManager;
		}

		this.setState({
			buttonProps: {
				disabled: !drawingManager?.hasSelection(),
			},
		});
	}

	/** Detaches the selection listener from the subscribed drawing manager, if there is one. */
	private unsubscribeFromSelectionChanged(): void {
		this.subscribedDrawingManager?.removeListener('selectionChanged', this.handleSelectionChanged);
		this.subscribedDrawingManager = undefined;
	}

	/**
	 * Enables the button while the renderer holds a selection and disables it once it is empty.
	 *
	 * @param selection - The renderer's selection as of this change.
	 */
	private readonly handleSelectionChanged = (selection: ISelection): void => {
		this.setState({ buttonProps: { disabled: selection.isEmpty } });
	};

	public onActiveStateChanged() {
		// Empty on purpose
	}

	public onKeyEvent(): void {
		this.clearSelection();
	}

	public onClick(): void {
		this.clearSelection();
	}

	private clearSelection() {
		this.context?.drawingManager?.interactionState?.clearSelection();
		this.emitToolUsed();
	}
}

ClearSelectionToolbarItem.contextType = DrawingManagerContext;
